import { useState, useEffect } from 'react';
import { CLINIC_NAME, CLINIC_TAGLINE } from '../constants';

const LINKS = [
  { id: 'home', label: 'Home' },
  { id: 'practitioners', label: 'For Practitioners' },
  { id: 'patients', label: 'For Patients' },
  { id: 'about', label: 'About Us' },
  { id: 'contact', label: 'Contact' },
];

export default function Navbar({ active, onNav }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const go = (id) => { onNav(id); setOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <>
      <style>{`
        .nb {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          transition: all 0.3s;
          background: ${scrolled ? 'rgba(255,251,245,0.98)' : 'rgba(255,251,245,0.94)'};
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid ${scrolled ? 'var(--border)' : 'transparent'};
          box-shadow: ${scrolled ? '0 4px 18px rgba(15,30,55,0.08)' : '0 1px 0 rgba(15,30,55,0.04)'};
        }
        .nb-inner {
          max-width: 1100px; margin: 0 auto; padding: 0 24px;
          height: 88px; display: flex; align-items: center; justify-content: space-between;
        }
        .nb-logo {
          display: flex; align-items: center; gap: 16px; cursor: pointer;
          transition: transform 0.25s ease;
        }
        .nb-logo:hover { transform: translateY(-1px); }
        /* SVG Logo icon */
        .nb-logo-icon {
          width: 54px; height: 54px; flex-shrink: 0;
          filter: drop-shadow(0 8px 18px rgba(30,58,95,0.32));
          transition: transform 0.3s ease, filter 0.3s ease;
        }
        .nb-logo:hover .nb-logo-icon {
          transform: rotate(-6deg) scale(1.05);
          filter: drop-shadow(0 10px 22px rgba(30,58,95,0.45));
        }
        .nb-logo-text { display: flex; flex-direction: column; }

        /* ── Editorial-serif wordmark ── */
        .nb-logo-name {
          font-family: var(--font-display);
          font-size: 32px; font-weight: 700;
          color: #0f1d33;
          line-height: 1; letter-spacing: -0.012em;
          display: inline-flex; align-items: center;
        }
        .nb-logo-name .nb-rule {
          display: inline-block;
          width: 26px; height: 2px;
          background: #c8b89a;
          margin: 4px 12px 0;
          flex-shrink: 0;
          border-radius: 1px;
        }
        .nb-logo-name .accent {
          font-style: italic; font-weight: 700;
          background: linear-gradient(135deg, #ee7960 0%, #d6604a 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }
        .nb-logo-sub {
          display: inline-flex; align-items: center; gap: 10px;
          font-family: var(--font-body);
          font-size: 11.5px; letter-spacing: 0.30em; text-transform: uppercase;
          color: var(--text-mid); font-weight: 700;
          margin-top: 9px;
        }
        .nb-logo-sub::before {
          content: ''; width: 20px; height: 2px;
          background: var(--accent); flex-shrink: 0;
          border-radius: 1px;
        }

        @media (max-width: 768px) {
          .nb-inner { height: 78px; }
          .nb-logo-icon { width: 46px; height: 46px; }
          .nb-logo-name { font-size: 26px; }
          .nb-logo-name .nb-rule { width: 18px; margin: 3px 9px 0; }
          .nb-logo-sub { font-size: 9.5px; letter-spacing: 0.24em; margin-top: 6px; }
          .nb-logo-sub::before { width: 14px; }
          .nb-logo { gap: 12px; }
        }
        @media (max-width: 480px) {
          .nb-inner { height: 70px; }
          .nb-logo-icon { width: 40px; height: 40px; }
          .nb-logo-name { font-size: 22px; }
          .nb-logo-name .nb-rule { display: none; }
          .nb-logo-name .accent::before { content: '\u00B7\u00A0'; color: #c8b89a; font-style: normal; }
        }
        .nb-links { display: flex; align-items: center; gap: 2px; }
        .nb-link {
          padding: 6px 13px; border-radius: 5px;
          font-size: 13.5px; color: var(--text-mid);
          cursor: pointer; transition: all 0.18s;
          background: none; border: none; font-family: var(--font-body);
        }
        .nb-link:hover { color: var(--charcoal); background: var(--sand-mid); }
        .nb-link.act { color: var(--accent); font-weight: 600; }
        .nb-cta {
          padding: 8px 18px; border-radius: var(--radius-sm);
          background: var(--terra); color: white;
          font-size: 13.5px; font-weight: 500; font-family: var(--font-body);
          border: none; cursor: pointer; transition: all 0.2s; margin-left: 8px;
          white-space: nowrap;
        }
        .nb-cta:hover { background: var(--terra-dark); }
        .nb-burger {
          display: none; flex-direction: column; gap: 5px;
          cursor: pointer; padding: 4px; background: none; border: none;
        }
        .nb-burger span {
          display: block; width: 22px; height: 2px;
          background: var(--charcoal); border-radius: 2px; transition: all 0.3s;
        }
        .mob-menu {
          display: none; position: fixed;
          top: 88px; left: 0; right: 0; bottom: 0;
          background: rgba(253,250,242,0.98); backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          z-index: 999; flex-direction: column;
          padding: 24px 20px; gap: 4px; overflow-y: auto;
          border-top: 1px solid var(--border);
        }
        @media (max-width: 768px) { .mob-menu { top: 78px; } }
        @media (max-width: 480px) { .mob-menu { top: 70px; } }
        .mob-menu.open { display: flex; }
        .mob-link {
          padding: 14px 16px; border-radius: var(--radius-sm);
          font-size: 16px; color: var(--charcoal);
          cursor: pointer; background: none; border: none;
          font-family: var(--font-body); text-align: left;
          border-bottom: 1px solid var(--sand-mid); transition: all 0.15s;
        }
        .mob-link.act { color: var(--accent); font-weight: 600; }
        .mob-cta {
          margin-top: 16px; padding: 14px; border-radius: var(--radius-sm);
          background: var(--terra); color: white;
          font-size: 15px; font-weight: 500; text-align: center;
          border: none; font-family: var(--font-body); cursor: pointer;
        }
        @media (max-width: 900px) {
          .nb-links { display: none; }
          .nb-burger { display: flex; }
        }
        @media (max-width: 768px) { .nb-inner { padding: 0 16px; } }
      `}</style>

      <nav className="nb">
        <div className="nb-inner">
          <div className="nb-logo" onClick={() => go('home')}>
            {/* Custom medical cross logo */}
            <svg className="nb-logo-icon" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="nb-logo-bg" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#2c5687" />
                  <stop offset="100%" stopColor="#13243d" />
                </linearGradient>
              </defs>
              <rect width="38" height="38" rx="9" fill="url(#nb-logo-bg)"/>
              <rect x="15" y="8" width="8" height="22" rx="3" fill="white"/>
              <rect x="8" y="15" width="22" height="8" rx="3" fill="white"/>
            </svg>
            <div className="nb-logo-text">
              <span className="nb-logo-name">
                {(() => {
                  const parts = CLINIC_NAME.split(' - ');
                  if (parts.length < 2) return CLINIC_NAME;
                  const [head, ...tail] = parts;
                  return (
                    <>
                      {head}
                      <span className="nb-rule" aria-hidden="true" />
                      <span className="accent">{tail.join(' - ')}</span>
                    </>
                  );
                })()}
              </span>
              <span className="nb-logo-sub">{CLINIC_TAGLINE}</span>
            </div>
          </div>

          <div className="nb-links">
            {LINKS.map(l => (
              <button key={l.id} className={`nb-link ${active === l.id ? 'act' : ''}`} onClick={() => go(l.id)}>
                {l.label}
              </button>
            ))}
            <button className="nb-cta" onClick={() => go('contact')}>Enquire Now</button>
          </div>

          <button className="nb-burger" onClick={() => setOpen(o => !o)}>
            <span style={{ transform: open ? 'rotate(45deg) translate(5px,5px)' : 'none' }} />
            <span style={{ opacity: open ? 0 : 1 }} />
            <span style={{ transform: open ? 'rotate(-45deg) translate(5px,-5px)' : 'none' }} />
          </button>
        </div>
      </nav>

      <div className={`mob-menu ${open ? 'open' : ''}`}>
        {LINKS.map(l => (
          <button key={l.id} className={`mob-link ${active === l.id ? 'act' : ''}`} onClick={() => go(l.id)}>
            {l.label}
          </button>
        ))}
        <button className="mob-cta" onClick={() => go('contact')}>Enquire Now</button>
      </div>
    </>
  );
}
